import { Provider } from 'react-redux';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ToastProvider } from './components/common/ToastProvider';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { RoleBasedRoute } from './components/layout/RoleBasedRoute';
import { DefaultRedirect } from './components/layout/DefaultRedirect';
import { HomePage } from './pages/Home';
import { NotFoundPage } from './pages/NotFound';
import { LoginPage } from './pages/auth/Login';
import { DashboardPage } from './pages/dashboard/Dashboard';
import { JobsPage } from './pages/jobs/Jobs';
import { JobCreatePage } from './pages/jobs/JobCreatePage';
import { JobDetailsPage } from './pages/jobs/JobDetailsPage';
import { JobEditPage } from './pages/jobs/JobEditPage';
import { OrdersPage } from './pages/orders/Orders';
import { ProductionPage } from './pages/production/Production';
import { DeliveryPage } from './pages/delivery/Delivery';
// CalendarPage removed - Calendar is now integrated in DeliveryCalendarPage
// New pages
import { DeliveryCalendarPage } from './pages/delivery-calendar/DeliveryCalendar';
import { HolidayCalendarPage } from './pages/holiday-calendar/HolidayCalendar';
import { UsersPage, UserFormPage, UserDetailsPage } from './pages/users';
import { RoleListPage, RoleFormPage, RoleDetailsPage } from './pages/roles';
import { QuotesPage } from './pages/quotes/Quotes';
import { PalletManagementPage } from './pages/pallet/PalletManagement';
import { CartlinksPage } from './pages/cartlinks/Cartlinks';
import { DeliveryManagementPage } from './pages/delivery-management/DeliveryManagement';
import { DeliveryPostcodeManagementPage } from './pages/delivery-postcode-management/DeliveryPostcodeManagement';
import { ProductionLinePage } from './pages/production-line/ProductionLine';
import { GlassArrivalJobsPage } from './pages/glass-arrival-jobs/GlassArrivalJobs';
import { DeliveryPageComponent } from './pages/delivery-page/DeliveryPage';
import { ProductionDashboardPage } from './pages/production-dashboard/ProductionDashboard';
import { HolidaysPage } from './pages/holidays/Holidays';
import { OvertimePage } from './pages/overtime/Overtime';
import { store } from './store';
import { GlassValuesPage } from './pages/glass-values/GlassValuesPage';
import { TestPdfPage } from './pages/test/TestPdfPage';
import { QuoteCreatePage } from './pages/quotes/QuoteCreatePage';
import { QuoteEditPage } from './pages/quotes/QuoteEditPage';
import { DeliveryJobs } from './pages/delivery-jobs/DeliveryJobs';
import { DeliveryJobDetails } from './pages/delivery-jobs/DeliveryJobDetails';

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ToastProvider />
          <Routes>
            {/* Public routes wrapped with public Layout */}
            <Route path="/login" element={<Layout><LoginPage /></Layout>} />
            
            {/* Root redirect - authenticated users get redirected based on role */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DefaultRedirect />
                </ProtectedRoute>
              }
            />

            {/* Protected routes use AppShell internally, no public Layout */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs"
              element={
                <ProtectedRoute>
                  <RoleBasedRoute
                    deliveryComponent={<DeliveryJobs />}
                    defaultComponent={<JobsPage />}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs/:id"
              element={
                <ProtectedRoute>
                  <RoleBasedRoute
                    deliveryComponent={<DeliveryJobDetails />}
                    defaultComponent={<JobDetailsPage />}
                  />
                </ProtectedRoute>
              }
            />
            {/* Deep link directly to Orders panel within Job Details */}
            <Route
              path="/jobs/:id/orders"
              element={
                <ProtectedRoute>
                  <RoleBasedRoute
                    deliveryComponent={<DeliveryJobDetails />}
                    defaultComponent={<JobDetailsPage />}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs/:id/edit"
              element={
                <ProtectedRoute>
                  <JobEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs/new"
              element={
                <ProtectedRoute>
                  <JobCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <OrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/production"
              element={
                <ProtectedRoute>
                  <ProductionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/delivery"
              element={
                <ProtectedRoute>
                  <DeliveryPage />
                </ProtectedRoute>
              }
            />
            {/* Calendar integrated into /delivery-calendar - see DeliveryCalendarPage */}
            {/* New routes */}
            <Route
              path="/delivery-calendar"
              element={
                <ProtectedRoute>
                  <DeliveryCalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/holiday-calendar"
              element={
                <ProtectedRoute>
                  <HolidayCalendarPage />
                </ProtectedRoute>
              }
            />
            {/* User Management Routes */}
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/create"
              element={
                <ProtectedRoute>
                  <UserFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:id"
              element={
                <ProtectedRoute>
                  <UserDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:id/edit"
              element={
                <ProtectedRoute>
                  <UserFormPage />
                </ProtectedRoute>
              }
            />
            {/* Role Management Routes */}
            <Route
              path="/roles"
              element={
                <ProtectedRoute>
                  <RoleListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roles/new"
              element={
                <ProtectedRoute>
                  <RoleFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roles/:id"
              element={
                <ProtectedRoute>
                  <RoleDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roles/:id/edit"
              element={
                <ProtectedRoute>
                  <RoleFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotes"
              element={
                <ProtectedRoute>
                  <QuotesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pallet-management"
              element={
                <ProtectedRoute>
                  <PalletManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotes/new"
              element={
                <ProtectedRoute>
                  <QuoteCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotes/:id/edit"
              element={
                <ProtectedRoute>
                  <QuoteEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cartlinks"
              element={
                <ProtectedRoute>
                  <CartlinksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/delivery-management"
              element={
                <ProtectedRoute>
                  <DeliveryManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/delivery-postcode-management"
              element={
                <ProtectedRoute>
                  <DeliveryPostcodeManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/production-line"
              element={
                <ProtectedRoute>
                  <ProductionLinePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/production-line/:tab"
              element={
                <ProtectedRoute>
                  <ProductionLinePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/glass-arrival-jobs"
              element={
                <ProtectedRoute>
                  <GlassArrivalJobsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/delivery-page"
              element={
                <ProtectedRoute>
                  <DeliveryPageComponent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/glass-values"
              element={
                <ProtectedRoute>
                  <GlassValuesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/production-dashboard"
              element={
                <ProtectedRoute>
                  <ProductionDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/holidays"
              element={
                <ProtectedRoute>
                  <HolidaysPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/overtime"
              element={
                <ProtectedRoute>
                  <OvertimePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/test/pdf"
              element={
                <ProtectedRoute>
                  <TestPdfPage />
                </ProtectedRoute>
              }
            />

            {/* Not found */}
            <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
          </Routes>
        </Router>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
